
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Eye, Trash2, Edit, Plus } from "lucide-react";
import { Room } from "@/hooks/useRooms";
import { useRoomServices } from "@/hooks/useRoomServices";
import { useDeleteService } from "@/hooks/useServices";
import AddServiceDialog from "./AddServiceDialog";
import EditServiceDialog from "./EditServiceDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface RoomServicesDialogProps {
  room: Room;
  onServicesUpdated: () => void;
}

const RoomServicesDialog = ({ room, onServicesUpdated }: RoomServicesDialogProps) => {
  const [open, setOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<any>(null);
  const { data: roomServices, refetch: refetchRoomServices } = useRoomServices(room.id);
  const deleteService = useDeleteService();

  const handleServiceAdded = () => {
    refetchRoomServices();
    onServicesUpdated();
  };

  const handleServiceUpdated = () => {
    refetchRoomServices();
    onServicesUpdated();
    setEditDialogOpen(false);
    setSelectedService(null);
  };

  const handleDeleteService = async (serviceId: string) => {
    try {
      await deleteService.mutateAsync(serviceId);
      refetchRoomServices();
      onServicesUpdated();
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  const handleEditClick = (service: any) => {
    setSelectedService(service);
    setEditDialogOpen(true);
  };

  const servicesCount = roomServices?.length || 0;

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="w-full justify-center">
            <Eye className="h-4 w-4 mr-2" />
            View Services ({servicesCount})
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Services - {room.name}</DialogTitle>
            <DialogDescription>
              Manage services available in this room. Services define what customers can book.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <h4 className="text-sm font-medium">Current Services</h4>
              <AddServiceDialog 
                providerId={room.provider_id}
                rooms={[room]}
                onServiceAdded={handleServiceAdded}
              />
            </div>

            {roomServices && roomServices.length > 0 ? (
              <div className="space-y-3">
                {roomServices.map((service) => (
                  <div key={service.id} className="flex flex-col p-3 border rounded-lg gap-3">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h5 className="font-medium">{service.name}</h5>
                        <Badge variant="secondary">
                          {service.currency} {service.price}
                        </Badge>
                        <Badge variant="outline">
                          {service.duration_minutes}min
                        </Badge>
                      </div>
                      {service.description && (
                        <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                      )}
                      <div className="text-xs text-gray-500">
                        Duration: {service.duration_minutes} minutes
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditClick(service)}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Service</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{service.name}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDeleteService(service.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete Service
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Eye className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No services added to this room yet</p>
                <p className="text-sm">Add a service to start accepting bookings</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {selectedService && (
        <EditServiceDialog
          service={selectedService}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onServiceUpdated={handleServiceUpdated}
        />
      )}
    </>
  );
};

export default RoomServicesDialog;
